import * as React from "react";
import { RefreshCw } from "lucide-react";

import { Button } from "@/components/ui/button";

/**
 * A page whose code chunk no longer exists on the server.
 *
 * Every route is code-split, and a deploy replaces the whole dist. A tab that
 * was open across a deploy is still running the previous index.html, so it asks
 * for chunk names that have been removed. The browser reports that as one of
 * half a dozen unhelpful messages — the one that started this was
 *
 *     'text/html' is not a valid JavaScript MIME type.
 *
 * which is true, names nothing an operator can act on, and looks like an
 * application defect. It is not one: the running code is simply older than the
 * server.
 *
 * The distinction matters because the response is different. A failed API call
 * is retried; a stale build cannot be retried at all — the chunk it wants is
 * gone for good, and only a reload can fix it.
 */

/**
 * Browser messages for a dynamic import that could not be loaded.
 *
 * Every engine words this differently and none of them expose a code, so
 * matching the text is the only option available. The list is deliberately
 * narrow: a false positive here would reload the page on an unrelated crash and
 * hide a real bug behind an infinite-looking refresh.
 */
const STALE_BUILD_PATTERNS: RegExp[] = [
  // Chrome / Edge, and Vite's own preload helper.
  /failed to fetch dynamically imported module/i,
  /error loading dynamically imported module/i, // Firefox
  /importing a module script failed/i, // Safari
  // The MIME rejection: index.html was served where a module was expected.
  /is not a valid javascript mime type/i,
  /expected a javascript(?:-or-wasm)? module script/i,
  // Bundler-level chunk loaders.
  /loading chunk \S+ failed/i,
  /loading css chunk \S+ failed/i,
];

/** Whether this error is a page whose chunk is no longer on the server. */
export function isStaleBuildError(error: unknown): boolean {
  const message = error instanceof Error ? error.message : typeof error === "string" ? error : "";
  if (message === "") return false;
  return STALE_BUILD_PATTERNS.some((re) => re.test(message));
}

/**
 * The key that stops a reload loop.
 *
 * If a reload does not fix it — a genuinely broken deploy, a proxy serving the
 * wrong root — reloading again will not either, and a page that reloads forever
 * is worse than a page that explains itself. One automatic attempt, then the
 * notice below with a manual button.
 *
 * sessionStorage, not localStorage: the guard belongs to this tab and must not
 * outlive it, or the next stale build in a week's time gets no auto-reload.
 */
const RELOAD_GUARD_KEY = "sp-ms365:stale-build-reloaded";

/** Whether this tab has already reloaded itself for a stale build. */
export function hasAlreadyReloaded(): boolean {
  try {
    return window.sessionStorage.getItem(RELOAD_GUARD_KEY) !== null;
  } catch {
    // Storage can throw outright (Safari private mode, blocked site data).
    // Treat that as "already reloaded": no auto-reload is a worse experience
    // than a loop is a risk.
    return true;
  }
}

/**
 * Reload once to pick up the current index.html, and record that we did.
 *
 * Returns whether a reload was started, so the caller can render the notice
 * when it was not.
 */
export function reloadForStaleBuild(): boolean {
  if (hasAlreadyReloaded()) return false;
  try {
    window.sessionStorage.setItem(RELOAD_GUARD_KEY, String(Date.now()));
  } catch {
    return false;
  }
  window.location.reload();
  return true;
}

/** Clears the guard. Called once the app has rendered a page successfully. */
export function clearStaleBuildGuard(): void {
  try {
    window.sessionStorage.removeItem(RELOAD_GUARD_KEY);
  } catch {
    // Nothing to clear if storage is unavailable.
  }
}

export interface StaleBuildNoticeProps {
  /** The underlying error, shown verbatim as the technical detail. */
  error: unknown;
}

/**
 * Shown when the automatic reload has already been tried and the page is still
 * asking for a chunk that is not there.
 */
export function StaleBuildNotice({ error }: StaleBuildNoticeProps): React.ReactElement {
  const detail = error instanceof Error ? error.message : String(error);
  return (
    <div
      role="alert"
      data-testid="stale-build-notice"
      className="flex flex-col gap-3 rounded-lg border border-border bg-card p-4"
    >
      <div className="flex flex-col gap-1">
        <p className="text-sm font-semibold text-ink">This page is from an older version</p>
        <p className="max-w-prose text-sm text-ink-2">
          The console was redeployed while this tab was open, so the code this page needs is no
          longer on the server. Nothing is wrong with the deployment and no data is affected —
          reloading loads the current version.
        </p>
      </div>

      <div>
        <Button
          variant="outline"
          size="sm"
          onClick={() => window.location.reload()}
          data-testid="stale-build-reload"
        >
          <RefreshCw aria-hidden="true" />
          Reload
        </Button>
      </div>

      <p className="text-xs text-ink-3" data-testid="stale-build-detail">
        <span className="font-mono">{detail}</span>
      </p>
    </div>
  );
}
